import { action, computed, makeObservable } from 'mobx';

import type { TPlanVersion } from '../comparison/types';
import { PlanVersionType } from '../comparison/types';
import type { DraftsStore } from '../drafts/drafts-store';

import type { PlanVersionStore } from './plan-version-store';

/**
 * Заготовка под будущий сервис версий ковра, объединяющий в себе функционал текущего стора версий и стора драфтов
 */
export class PlanVersionsService {
  private readonly draftsStore: DraftsStore;
  private readonly depPlanVersionsStore: PlanVersionStore;

  constructor(draftsStore: DraftsStore, depPlanVersionsStore: PlanVersionStore) {
    this.draftsStore = draftsStore;
    this.depPlanVersionsStore = depPlanVersionsStore;

    makeObservable(this);
  }

  @computed
  get currentPlanVersion(): TPlanVersion | null {
    return this.depPlanVersionsStore.version ?? null;
  }

  @computed
  get versionsList(): TPlanVersion[] {
    return [...this.depPlanVersionsStore.actualVersions, ...this.depPlanVersionsStore.archivedVersions];
  }

  @computed
  get currentDraft(): TPlanVersion | null {
    return this.draftsStore.draft ?? null;
  }

  @computed
  get actualPlanVersions(): TPlanVersion[] {
    return this.depPlanVersionsStore.actualVersions;
  }

  @computed
  get currentDraftId(): number | null {
    return this.draftsStore.draftVersionId;
  }

  @computed
  get draftsList(): TPlanVersion[] {
    return this.draftsStore.draftsList;
  }

  @computed
  get currentVersion(): TPlanVersion | null {
    return this.draftsStore.draft ?? this.depPlanVersionsStore.version ?? null;
  }

  @computed
  get publicDraft(): TPlanVersion | null {
    return this.depPlanVersionsStore.actualVersions.find((v) => v.data.versionType === PlanVersionType.public) ?? null;
  }

  @action.bound
  setDraft(id: number): void {
    this.draftsStore.setDraft(id);

    const draft = this.draftsList.find((d) => d.id === id);

    if (!draft) {
      console.error('cannot find corresponding draft object', id);
      return;
    }

    const parentVersion = this.depPlanVersionsStore.actualVersions.find((v) => v.id === draft.data.parentVersionId);

    if (!parentVersion) {
      console.error('cannot find corresponding parent version', draft);
      return;
    }

    this.depPlanVersionsStore.setVersion(parentVersion);
  }

  deleteDraftById = async (draftVersionId: number) => await this.draftsStore.deleteDraftById(draftVersionId);

  fetchDraftsList = async () => await this.draftsStore.fetchDraftsList();

  isDraftOfPublicDraftVersion = (version: number | TPlanVersion): boolean => {
    const draft = typeof version === 'number' ? this.draftsStore.draftsList.find((d) => d.id === version) : version;

    if (!draft) {
      return false;
    }

    const correspondingPlanVersion = this.depPlanVersionsStore.actualVersions.find(
      (v) => v.id === draft.data.parentVersionId,
    );

    return correspondingPlanVersion?.data.versionType === PlanVersionType.public;
  };

  init = async () => {
    await Promise.all([this.draftsStore.init(), this.depPlanVersionsStore.init()]);
  };
}
